<template>
  <b-container fluid>
    <b-card class="mt-2" title="Margin simulation">
      <b-row>
        <b-col>
          <b-form-group
            id="input-group-portfolio"
            label="Portfolio:"
            label-for="input-portfolio"
            description="Base portfolio for simulation"
          >
            <b-form-select
              id="input-portfolio"
              v-model="portfolioMode"
              :options="portfolioModes"
              @change="onPortfolioChange"
            ></b-form-select>
          </b-form-group>
        </b-col>
        <b-col>
          <button
            pill
            class="action-btn"
            id="add-trade"
            type="button"
            @click="openTradeModal"
          >
            <font-awesome-icon icon="plus" />
            Add trade
          </button>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <button
            pill
            class="action-btn"
            id="upload-fpml"
            type="button"
            @click="openFpMLModal"
          >
            <font-awesome-icon icon="plus" />
            Upload FpML
          </button>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-table
            ref="table"
            class="table-dark table-btn-cell"
            striped
            hover
            :fields="fields"
            :items="items"
          >
            <!-- Only what if trades can be edited or removed. -->
            <template v-slot:cell(action)="data">
              <button
                v-b-tooltip.hover
                title="Duplicate trade"
                class="action-btn"
                @click="duplicateTrade(data.index)"
              >
                <font-awesome-icon icon="copy" />
              </button>
              <button
                v-b-tooltip.hover
                title="Edit trade"
                v-if="data.item.whatif"
                class="action-btn ml-2"
                @click="editTrade(data.index)"
              >
                <font-awesome-icon icon="edit" />
              </button>

              <button
                v-b-tooltip.hover
                title="Remove trade"
                v-if="data.item.whatif"
                class="action-btn ml-2"
                @click="removeTrade(data.index)"
              >
                <font-awesome-icon icon="trash" />
              </button>
            </template>
          </b-table>
        </b-col>
      </b-row>
      <b-row>
        <b-btn @click="runCalculation" :disabled="items.length == 0"
          >Run simulation</b-btn
        >
      </b-row>

      <b-row class="mt-5" v-if="hasResults">
        <b-col>
          <collapsable content="Combined results" :visible="true">
            <RiskDetails
              :key="componentKey"
              :results="results.combinedResults"
            />
          </collapsable>
        </b-col>

        <b-col>
          <!-- only shows if there are whatif trades -->
          <collapsable
            v-for="(result, i) in results.resultsByTrade"
            v-bind:key="i"
            :content="`Trade ${result[0]} results`"
          >
            <RiskDetails :results="result[1]" />
          </collapsable>
        </b-col>
      </b-row>
    </b-card>

    <div>
      <b-modal no-close-on-backdrop size="xl" id="add-trade-modal" hide-footer>
        <template v-slot:modal-title>
          Add new trade
        </template>

        <AddTrade ref="draft" />

        <b-button variant="success" class="mt-3" @click="addTrade"
          >Add trade</b-button
        >
        <b-button class="mt-3 ml-2" @click="$bvModal.hide('add-trade-modal')"
          >Close Me</b-button
        >
      </b-modal>
    </div>

    <div>
      <b-modal
        no-close-on-backdrop
        size="xl"
        id="upload-fpml-modal"
        hide-footer
      >
        <template v-slot:modal-title>Upload trade as FpML</template>

        <FpMLUpload ref="asd" />

        <b-button variant="success" class="mt-3" @click="parseFile"
          >Upload FpML</b-button
        >
        <b-button class="mt-3 ml-2" @click="$bvModal.hide('upload-fpml-modal')"
          >Close Me</b-button
        >
      </b-modal>
    </div>

    <b-form-file v-model="file" class="mt-3" plain></b-form-file>
    <div class="mt-3">Selected file: {{ file ? file.name : "" }}</div>
    <b-btn @click="parseFile">I AM A BUTTON</b-btn>
  </b-container>
</template>

<script>
import AddTrade from "@/components/AddTrade.vue";
import RiskDetails from "@/components/RiskDetails.vue";
import FpMLUpload from "@/components/FpMLUpload.vue";

/*
    Full swap to something realistic to display.
*/
function parseSwap(swap) {
  let legs = swap.legs;

  // e.g. Pay fix 0.0023 P6M
  let payLeg = legs.filter(leg => leg.payReceive == "Pay")[0];

  // e.g. Rec TAIBOR6M
  let recLeg = legs.filter(leg => leg.payReceive == "Receive")[0];
  let tradeData = {
    id: swap.id,
    counterparty: swap.counterparty,
    rate: 0.0,
    startDate: "",
    endDate: "",
    index: "",
    direction: "",
    notional: "",
    currency: "",
    whatif: false,
    componentKey: 0
  };
  tradeData.startDate = payLeg.accrualSchedule.startDate;
  tradeData.endDate = payLeg.accrualSchedule.endDate;

  tradeData.notional = payLeg.notionalSchedule.amount;
  tradeData.currency = payLeg.notionalSchedule.currency;
  // Buy means receive the Floating leg. Sell means pay the floating leg.
  if (payLeg.calculation.index !== undefined) {
    tradeData.direction = "SELL";
    tradeData.index = payLeg.calculation.index;
    tradeData.rate = recLeg.calculation.rate;
  } else {
    tradeData.direction = "BUY";
    tradeData.index = recLeg.calculation.index;
    tradeData.rate = payLeg.calculation.rate;
  }

  return tradeData;
}
export default {
  data() {
    return {
      portfolioMode: "empty",
      portfolioModes: [{ value: "empty", text: "Empty portfolio" }],
      fields: [
        { key: "id", sortable: true },
        { key: "counterparty", sortable: true },
        { key: "startDate", sortable: true },
        { key: "endDate", sortable: true },
        { key: "direction", sortable: true },
        { key: "notional", sortable: true },
        { key: "currency", sortable: true },
        { key: "index", sortable: true },
        { key: "rate", sortable: true },
        { key: "whatif", sortable: true },
        { key: "action" }
      ],
      items: [],
      hasResults: false,
      results: null,
      // ID of the trade being edited. If this is null, then we are editing a new trade
      currentlyEditing: null,
      currentWhatIf: 0,

      file: null
    };
  },
  async mounted() {
    let resp = await this.$client.getMembers();
    resp.forEach(member => {
      this.portfolioModes.push({ value: member, text: member });
    });
  },
  methods: {
    async parseFile() {
      let resp = await this.$client.parseFpML(this.file);
      let trades = resp.map(parseSwap);
      trades.forEach(t => {
        t.id = this.getNextWhatifId();
        t.whatif = true;
        this.items.unshift(t);
      });
      // this.closeFpMLModal();
    },
    openFpMLModal() {
      this.$bvModal.show("upload-fpml-modal");
    },
    closeFpMLModal() {
      // this.currentlyEditing = null;
      this.$$bvModal.hide("upload-fpml-modal");
    },
    async onPortfolioChange() {
      if (this.portfolioMode !== null && this.portfolioMode != "empty") {
        let resp = await this.$client.getTradesForMember(this.portfolioMode);
        this.items = resp.map(parseSwap);
        this.currentWhatIf = 0;
      } else {
        this.items = [];
      }
    },
    openTradeModal() {
      this.currentlyEditing = null;
      this.$bvModal.show("add-trade-modal");
    },
    addTrade() {
      let trade = this.$refs.draft.getTrade();
      if (trade != null) {
        trade.whatif = true;
        if (this.currentlyEditing == null) {
          trade.id = this.getNextWhatifId();
          this.items.unshift(trade);
        } else {
          this.items[this.currentlyEditing] = trade;
          this.$refs.table.refresh();
        }
        this.$bvModal.hide("add-trade-modal");
      }
    },
    editTrade(index) {
      this.currentlyEditing = index;
      this.$bvModal.show("add-trade-modal");
      // modal is not yet created so $refs.draft is undefined
      this.$nextTick(() => {
        this.$refs.draft.setTrade(this.items[index]);
      });
    },
    removeTrade(index) {
      this.items.splice(index, 1);
    },
    duplicateTrade(index) {
      let copy = JSON.parse(JSON.stringify(this.items[index]));
      copy.whatif = true;
      copy.id = this.getNextWhatifId();
      copy.counterparty = "";
      this.items.unshift(copy);
    },
    getNextWhatifId() {
      const id = `whatif-${this.currentWhatIf}`;
      this.currentWhatIf += 1;
      return id;
    },
    async runCalculation() {
      let nodeInfo = null;
      if (this.portfolioMode != null && this.portfolioMode != "empty") {
        nodeInfo = {
          id: this.portfolioMode,
          nodeType: "PORTFOLIO"
        };
      }

      let trades = this.items
        .filter(t => t.whatif)
        .map(t => {
          let req = {
            index: t.index,
            rate: t.rate,
            notional: t.notional,
            currency: t.currency,
            startDate: t.startDate,
            endDate: t.endDate,
            direction: t.direction
          };
          return req;
        });

      const results = await this.$client.runSimulation(nodeInfo, trades);

      this.results = {
        combinedResults: results.combinedResults,
        resultsByTrade: this.items
          .filter(t => t.whatif)
          .map((t, index) => [t.id, results.resultsByTrade[index]])
      };
      this.hasResults = true;
      this.componentKey += 1;
    }
  },
  components: { AddTrade, RiskDetails, FpMLUpload }
};
</script>
<style lang="scss" scoped>
button#add-trade {
  position: absolute;
  right: 1em;
  bottom: 1em;
}
button#upload-fpml {
  position: absolute;
  right: 1em;
  bottom: 3em;
}

.table-btn-cell {
  text-align: center;
}

form {
  display: block;
  height: 400px;
  width: 400px;
  background: #ccc;
  margin: auto;
  margin-top: 40px;
  text-align: center;
  line-height: 400px;
  border-radius: 4px;
}
div.file-listing {
  width: 400px;
  margin: auto;
  padding: 10px;
  border-bottom: 1px solid #ddd;
}
</style>
